import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "income", subs: "" });

  const load = async () => {
    const data = await api.listCategories();
    setCats(data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await api.createCategory({
        name: form.name,
        type: form.type,
        subcategories: form.subs.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Categoría creada");
      setOpen(false);
      setForm({ name: "", type: "income", subs: "" });
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const handleDelete = async (c) => {
    if (c.is_default) { toast.error("No puedes eliminar categorías predefinidas"); return; }
    if (!window.confirm(`Eliminar "${c.name}"?`)) return;
    try {
      await api.deleteCategory(c.id);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error("Error");
    }
  };

  const incomes = cats.filter((c) => c.type === "income");
  const expenses = cats.filter((c) => c.type === "expense");

  return (
    <div>
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div>
          <div className="label-xs text-muted-foreground">Taxonomía</div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight">Categorías</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-category-btn" className="rounded-sm font-semibold">
              <Plus size={16} className="mr-2" weight="bold" /> Nueva Categoría
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-category-dialog" className="rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">
                Nueva Categoría
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="label-xs text-muted-foreground">Nombre</Label>
                <Input
                  data-testid="category-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="label-xs text-muted-foreground">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="category-type-select" className="mt-1 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Ingreso</SelectItem>
                    <SelectItem value="expense">Egreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-xs text-muted-foreground">Subcategorías (separadas por coma)</Label>
                <Input
                  data-testid="category-subs-input"
                  value={form.subs}
                  onChange={(e) => setForm({ ...form, subs: e.target.value })}
                  placeholder="Sub1, Sub2, Sub3"
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="confirm-new-category-btn" onClick={handleCreate} className="rounded-sm font-semibold">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CategorySection title="Ingresos" items={incomes} color="emerald" onDelete={handleDelete} />
        <CategorySection title="Egresos" items={expenses} color="red" onDelete={handleDelete} />
      </div>
    </div>
  );
}

function CategorySection({ title, items, color, onDelete }) {
  return (
    <div className="card-elevated">
      <div className={`px-5 py-3 border-b border-border flex items-center justify-between ${color === "emerald" ? "bg-emerald-50 dark:bg-emerald-950" : "bg-red-50 dark:bg-red-950"}`}>
        <h3 className="font-heading font-bold">{title}</h3>
        <span className="label-xs text-muted-foreground">{items.length}</span>
      </div>
      <div className="divide-y divide-border">
        {items.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">Vacío</div>}
        {items.map((c) => (
          <div key={c.id} className="p-4 flex items-start justify-between" data-testid={`category-${c.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Tag size={14} weight="duotone" className="text-muted-foreground" />
                <span className="font-semibold">{c.name}</span>
                {c.is_default && <span className="label-xs text-muted-foreground">Predefinida</span>}
              </div>
              {c.subcategories?.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  {c.subcategories.map((s) => (
                    <span key={s} className="label-xs px-2 py-0.5 rounded-sm bg-muted">{s}</span>
                  ))}
                </div>
              )}
            </div>
            {!c.is_default && (
              <button
                data-testid={`delete-category-${c.id}`}
                onClick={() => onDelete(c)}
                className="p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-muted"
              >
                <Trash size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
